// BL621 Phase 3 (Decision 042 §"Output flags") — the single source of truth for
// the `-o` / `--output` flag on read verbs. kubectl-style format parity:
//
//   -o table   human-friendly (default)
//   -o json    same shape as the underlying API response
//   -o yaml    same payload, serialized as YAML
//   -o wide    table plus extra columns (kubectl parity)
//
// Every read verb (get / describe / info / status / versions / whoami /
// env list / secrets list / hooks list) routes its format decision through
// here so the behavior — precedence, validation, error envelope — is uniform.

import type { Command } from 'commander';
import { stringify as stringifyYaml } from 'yaml';
import { CloudGridError } from '../utils.js';

export type OutputFormat = 'table' | 'json' | 'yaml' | 'wide';

export const OUTPUT_FORMATS: readonly OutputFormat[] = ['table', 'json', 'yaml', 'wide'];

/** The two formats whose payload is a structured serialization of the API response. */
export type StructuredFormat = 'json' | 'yaml';

export interface OutputFormatOpts {
  /** Raw value of the `-o` / `--output` flag (undefined when not passed). */
  output?: string;
  /**
   * Legacy `--json` boolean flag. Kept as a back-compatible alias so existing
   * scripts (and the pre-BL621 test suite) keep working: `--json` ≡ `-o json`.
   * An explicit `-o` always wins over `--json`.
   */
  json?: boolean;
}

/**
 * Validate a raw format string. Throws a §23-voice {@link CloudGridError} on an
 * unknown value so the user sees `Unknown output format 'xml'. Use one of: …`.
 */
export function parseOutputFormat(raw: string): OutputFormat {
  const v = raw.trim().toLowerCase();
  if ((OUTPUT_FORMATS as readonly string[]).includes(v)) return v as OutputFormat;
  throw new CloudGridError(
    `Unknown output format '${raw}'. Use one of: ${OUTPUT_FORMATS.join(', ')}.`,
  );
}

/**
 * Resolve the active format from the command options. Precedence:
 *   1. explicit `-o <format>`
 *   2. legacy `--json`
 *   3. `CLOUDGRID_OUTPUT` env var
 *   4. `table` (default)
 */
export function resolveOutputFormat(opts: OutputFormatOpts): OutputFormat {
  if (opts.output != null && opts.output !== '') return parseOutputFormat(opts.output);
  if (opts.json) return 'json';
  const env = process.env.CLOUDGRID_OUTPUT;
  if (env != null && env !== '') return parseOutputFormat(env);
  return 'table';
}

/**
 * Whether the resolved format is a machine-readable one (json|yaml). Used in
 * catch blocks to pick the error envelope, so it must never throw — an invalid
 * `-o` value resolves to `false` (the human error path), and the invalid-format
 * CloudGridError surfaces to stderr in its own right.
 */
export function isStructuredOutput(opts: OutputFormatOpts): boolean {
  const raw = (opts.output ?? (opts.json ? 'json' : process.env.CLOUDGRID_OUTPUT ?? ''))
    .trim()
    .toLowerCase();
  return raw === 'json' || raw === 'yaml';
}

/**
 * Serialize a structured payload for the json/yaml formats. JSON stays compact
 * (single line + trailing newline) to match the pre-BL621 `--json` output that
 * scripts already parse; YAML uses the `yaml` package's block style.
 */
export function serializeStructured(format: StructuredFormat, value: unknown): string {
  if (format === 'yaml') return stringifyYaml(value);
  return JSON.stringify(value) + '\n';
}

/**
 * The structured error envelope (Decision 042 §"Output flags"): printed to
 * STDOUT in json/yaml mode so `set -e` scripts can introspect the failure.
 */
export interface StructuredError {
  code: string;
  message: string;
  task_id?: string;
}

/** Render `{ error: { code, message, task_id? } }` for a structured-mode failure. */
export function structuredErrorPayload(err: StructuredError): { error: StructuredError } {
  const payload: StructuredError = { code: err.code, message: err.message };
  if (err.task_id !== undefined) payload.task_id = err.task_id;
  return { error: payload };
}

/**
 * Register the shared `-o, --output <format>` option on a command. No default is
 * set so {@link resolveOutputFormat} can fall back to `--json` / the env var
 * when `-o` is absent.
 */
export function addOutputOption(command: Command): Command {
  return command.option(
    '-o, --output <format>',
    `Output format: ${OUTPUT_FORMATS.join(' | ')}`,
  );
}
